/**
 * Componente MercadoPagoReturnHandler
 * Maneja específicamente el retorno desde Mercado Pago
 * Resuelve problemas de autenticación y redirección
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import mercadoPagoService from '../../services/mercadopago_compatible';

const MercadoPagoReturnHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { refreshSubscription } = useSubscription(user?.id);
  
  const [status, setStatus] = useState('loading'); // loading, processing, success, error
  const [message, setMessage] = useState('Procesando tu suscripción...');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preapprovalId = params.get('preapproval_id');
    const paymentId = params.get('payment_id');
    
    console.log('🔍 MercadoPagoReturnHandler - Parámetros:', {
      preapprovalId,
      paymentId,
      isAuthenticated,
      user: user?.id,
      authLoading,
      retryCount,
      status
    });

    // Si no hay parámetros de Mercado Pago, redirigir a premium
    if (!preapprovalId && !paymentId) {
      console.log('❌ No se encontraron parámetros de Mercado Pago');
      navigate('/premium', { replace: true });
      return;
    }

    // Evitar ejecución si ya terminamos el proceso
    if (status === 'success' || status === 'error') {
      console.log(`⏭️ Proceso terminado con estado: ${status}`);
      return;
    }

    // Si estamos procesando, no hacer nada
    if (status === 'processing') {
      console.log('⏳ Ya estamos procesando...');
      return;
    }

    // Función para procesar el retorno
    const processReturn = async () => {
      try {
        // Cambiar a processing solo cuando realmente vamos a procesar
        setStatus('processing');
        setMessage('Verificando tu autenticación...');

        // Esperar a que la autenticación se complete
        if (authLoading) {
          console.log('⏳ Esperando verificación de autenticación...');
          // Volver a loading para que se pueda reintentar
          setStatus('loading');
          return;
        }

        if (!isAuthenticated) {
          if (retryCount < 3) {
            console.log(`🔄 Intento ${retryCount + 1} - Usuario no autenticado, esperando...`);
            setRetryCount(prev => prev + 1);
            setStatus('loading'); // Volver a loading para reintentar
            // Esperar más tiempo y reintentar
            setTimeout(() => {
              // El useEffect se volverá a ejecutar con el nuevo retryCount
            }, 2000);
            return;
          } else {
            console.log('❌ Máximo de intentos alcanzado, redirigiendo a login');
            setStatus('error');
            setMessage('Redirigiendo a login...');
            setTimeout(() => {
              navigate('/auth/login', { 
                state: { from: location.pathname + location.search },
                replace: true 
              });
            }, 2000);
            return;
          }
        }

        // Usuario autenticado - procesar la suscripción
        setMessage('Actualizando tu suscripción...');
        console.log('✅ Usuario autenticado, actualizando suscripción en base de datos...');
        
        // Actualizar la suscripción directamente usando el servicio
        const updateResult = await mercadoPagoService.updateSubscriptionAfterReturn(preapprovalId);
        
        if (updateResult.success) {
          console.log('✅ Suscripción actualizada en la base de datos:', updateResult);
          setStatus('success');
          setMessage('¡Suscripción activada exitosamente!');
          
          // Refrescar el contexto de suscripción
          await refreshSubscription();
        } else {
          throw new Error(updateResult.error || 'Error al actualizar la suscripción');
        }
        
      } catch (error) {
        console.error('❌ Error procesando retorno de Mercado Pago:', error);
        setStatus('error');
        setMessage('Ocurrió un error al procesar tu suscripción. Por favor, contacta a soporte.');
        
        // Redirigir a premium después de un tiempo
        setTimeout(() => {
          navigate('/premium', { replace: true });
        }, 3000);
      }
    };

    // Ejecutar el procesamiento
    processReturn();
  }, [location, isAuthenticated, authLoading, user, retryCount, navigate, refreshSubscription]);

  // Renderizar estado de carga o éxito
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
      <div className="text-center max-w-md mx-auto px-4">
        {/* Icono de carga */}
        <div className="mb-6">
          {status === 'loading' || status === 'processing' ? (
            <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          ) : status === 'success' ? (
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ) : (
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}
        </div>

        {/* Mensaje principal */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {status === 'loading' || status === 'processing' ? 'Procesando...' : 
           status === 'success' ? '¡Suscripción Activada!' : 'Error'}
        </h1>
        
        <p className="text-gray-600 mb-4">
          {message}
        </p>

        {/* Información adicional */}
        {status === 'processing' && retryCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              Intento {retryCount + 1} de 3 - Verificando tu sesión...
            </p>
          </div>
        )}

        {/* Indicador de progreso */}
        {status === 'loading' || status === 'processing' ? (
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((retryCount + 1) * 25, 100)}%` }}
            ></div>
          </div>
        ) : null}

        {/* Acciones */}
        {status === 'success' && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/planes', { replace: true })}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Ver mis Planes
            </button>
            <button
              onClick={() => navigate('/premium', { replace: true })}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Ir a Premium
            </button>
          </div>
        )}

        {status === 'error' && (
          <button
            onClick={() => navigate('/premium', { replace: true })}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Ir a Premium
          </button>
        )}
      </div>
    </div>
  );
};

export default MercadoPagoReturnHandler;
